import * as React from "react";
import { Input } from "@/components/ui/input";

interface NumberFieldProps
  extends Omit<
    React.ComponentProps<typeof Input>,
    "value" | "onChange" | "min" | "max" | "type"
  > {
  /** Current committed numeric value (undefined/null renders empty). */
  value: number | undefined | null;
  /**
   * Called with the normalized value when the user commits the edit (blur).
   * While typing, the raw text stays visible and the caller value is NOT
   * updated. On blur the parsed value is clamped to [min, max]; when the
   * field is empty or not a number, `emptyValue` is committed (or undefined
   * when no `emptyValue` is set).
   */
  onValueChange: (value: number | undefined) => void;
  min?: number;
  max?: number;
  /**
   * Value committed when the field is empty/invalid. When set, an empty field
   * shows a hint explaining what will be saved instead of silently snapping.
   * When omitted, empty commits `undefined` (for optional fields).
   */
  emptyValue?: number;
  /** Base for hint testids; defaults to the input's data-testid. */
  hintTestId?: string;
  /**
   * Allow decimal values: parse with parseFloat instead of parseInt so
   * in-progress values like "9.99" commit with full precision.
   */
  allowDecimal?: boolean;
}

/**
 * Number input that keeps the raw typed text (including empty) while editing,
 * and only normalizes/clamps the committed value on blur. Out-of-range or
 * empty values show a small hint instead of being silently replaced.
 *
 * Forwards refs and any injected props (id, aria-*) to the inner Input, so it
 * can be used directly inside react-hook-form's <FormControl>.
 */
export const NumberField = React.forwardRef<HTMLInputElement, NumberFieldProps>(
  function NumberField(
    { value, onValueChange, min, max, emptyValue, hintTestId, allowDecimal, onBlur, onFocus, onKeyDown, ...props },
    ref,
  ) {
    const [text, setText] = React.useState<string>(
      value === undefined || value === null ? "" : String(value),
    );
    const [editing, setEditing] = React.useState(false);

    // Keep display in sync with external value when not actively editing
    React.useEffect(() => {
      if (!editing) {
        setText(value === undefined || value === null ? "" : String(value));
      }
    }, [value, editing]);

    const clamp = React.useCallback(
      (n: number) => {
        let r = n;
        if (min !== undefined) r = Math.max(min, r);
        if (max !== undefined) r = Math.min(max, r);
        return r;
      },
      [min, max],
    );

    const parsed = allowDecimal ? parseFloat(text) : parseInt(text, 10);
    const isEmpty = text.trim() === "" || Number.isNaN(parsed);
    const outOfRange =
      !isEmpty &&
      ((min !== undefined && parsed < min) || (max !== undefined && parsed > max));
    // When decimals aren't allowed, warn if the typed text has a fractional
    // part that parseInt will silently drop (e.g. "2.5" -> 2).
    const truncated =
      !isEmpty && !allowDecimal && parseFloat(text) !== parsed;
    const committed = isEmpty ? emptyValue : clamp(parsed);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      // Keep the raw typed value while editing; commit happens on blur/Enter.
      setText(e.target.value);
    };

    const commit = React.useCallback(
      (raw: string) => {
        const p = allowDecimal ? parseFloat(raw) : parseInt(raw, 10);
        if (Number.isNaN(p)) {
          onValueChange(emptyValue);
          setText(emptyValue === undefined ? "" : String(emptyValue));
        } else {
          const c = clamp(p);
          onValueChange(c);
          setText(String(c));
        }
      },
      [onValueChange, emptyValue, clamp, allowDecimal],
    );

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setEditing(false);
      commit(text);
      onBlur?.(e);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      // Flush the pending edit before Enter-triggered form submission so the
      // submitted payload reflects what the user typed (normalized/clamped).
      if (e.key === "Enter") {
        commit((e.target as HTMLInputElement).value);
      }
      onKeyDown?.(e);
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setEditing(true);
      onFocus?.(e);
    };

    const testIdBase =
      hintTestId ??
      ((props as Record<string, unknown>)["data-testid"] as string | undefined) ??
      "number-field";

    const rangeLabel =
      min !== undefined && max !== undefined
        ? `between ${min} and ${max}`
        : min !== undefined
          ? `at least ${min}`
          : max !== undefined
            ? `at most ${max}`
            : "";

    return (
      <>
        <Input
          type="number"
          min={min}
          max={max}
          step={allowDecimal ? "any" : undefined}
          value={text}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          ref={ref}
          {...props}
        />
        {outOfRange && (
          <p
            className="text-sm text-amber-600 dark:text-amber-400"
            data-testid={`${testIdBase}-clamp-hint`}
          >
            Must be {rangeLabel} — will be saved as {committed}.
          </p>
        )}
        {truncated && (
          <p
            className="text-sm text-amber-600 dark:text-amber-400"
            data-testid={`${testIdBase}-decimal-hint`}
          >
            Decimals aren&apos;t allowed here — will be saved as {committed}.
          </p>
        )}
        {editing && isEmpty && emptyValue !== undefined && (
          <p
            className="text-sm text-amber-600 dark:text-amber-400"
            data-testid={`${testIdBase}-empty-hint`}
          >
            Enter a number{rangeLabel ? ` (${rangeLabel})` : ""} — will be saved as{" "}
            {emptyValue} if left empty.
          </p>
        )}
      </>
    );
  },
);
